import { action, computed, makeObservable, observable } from "mobx";
import { startTransition, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type { NavigateFunction, SetURLSearchParams } from "react-router-dom";
import { isCommercialRolloutActive } from "@/modules/commercial-plans";
import { AppSubStore } from "@/store/types";
import type { AppSubStoreArgs } from "@/store/types";
import {
  AGENT_ACTIVITY_PROJECT_ID_SEARCH_PARAM,
  AGENT_ACTIVITY_ROUTINE_ID_SEARCH_PARAM,
  AGENT_ACTIVITY_TASK_ID_SEARCH_PARAM,
  AGENT_GOAL_ID_SEARCH_PARAM,
  AGENT_INFORMATION_ITEM_ID_SEARCH_PARAM,
  AGENT_PROJECT_ID_SEARCH_PARAM,
  AGENT_SEARCH_PARAM,
  AGENT_TASK_PROJECT_ID_SEARCH_PARAM,
  AGENT_TASK_ID_SEARCH_PARAM,
  AGENT_TASK_VIEW_SEARCH_PARAM,
  AgentSurfaceId,
} from "@/store/agent-overlay/types";
import type { AgentActivityContext, AgentOverlayState } from "@/store/agent-overlay/types";
import {
  withoutAgentIdentitySearchParams,
  withoutAgentSearchParams,
  withoutSettingsSearchParams,
} from "@/store/overlay-search-params";

const AGENT_OVERLAY_HISTORY_STATE_KEY = "agentOverlayEntry";
const AGENT_OVERLAY_HISTORY_DEPTH_STATE_KEY = "agentOverlayDepth";
const AGENT_OVERLAY_UNTRACKED_ROOT_STATE_KEY = "agentOverlayUntrackedRoot";

type AgentOverlayHistoryState = {
  [AGENT_OVERLAY_HISTORY_STATE_KEY]?: true;
  [AGENT_OVERLAY_HISTORY_DEPTH_STATE_KEY]?: number;
  [AGENT_OVERLAY_UNTRACKED_ROOT_STATE_KEY]?: true;
} & Record<string, unknown>;

const agentStatesMatch = ({
  currentState,
  nextState,
}: {
  currentState: AgentOverlayState | null;
  nextState: AgentOverlayState | null;
}) => {
  if (currentState?.surfaceId !== nextState?.surfaceId) {
    return false;
  }

  if (currentState === null || nextState === null) {
    return currentState === nextState;
  }

  switch (currentState.surfaceId) {
    case AgentSurfaceId.Goal:
      return (
        nextState.surfaceId === AgentSurfaceId.Goal && currentState.goalId === nextState.goalId
      );
    case AgentSurfaceId.Project:
      return (
        nextState.surfaceId === AgentSurfaceId.Project &&
        currentState.projectId === nextState.projectId
      );
    case AgentSurfaceId.ProjectTasks:
      return (
        nextState.surfaceId === AgentSurfaceId.ProjectTasks &&
        currentState.projectId === nextState.projectId
      );
    case AgentSurfaceId.Task:
      return (
        nextState.surfaceId === AgentSurfaceId.Task && currentState.taskId === nextState.taskId
      );
    case AgentSurfaceId.InformationItem:
      return (
        nextState.surfaceId === AgentSurfaceId.InformationItem &&
        currentState.informationItemId === nextState.informationItemId
      );
    default:
      return true;
  }
};

export class AgentOverlayStore extends AppSubStore {
  /**
   * The validated state from the `?agent=` query params. This is the source of
   * truth for whether the full-screen agent surface is open.
   */
  agentParam: AgentOverlayState | null = null;

  private setSearchParams: SetURLSearchParams | null = null;
  private navigate: NavigateFunction | null = null;
  private locationState: AgentOverlayHistoryState | null = null;
  private currentSearchParams: URLSearchParams | null = null;
  private shouldCloseUntrackedOverlayRoot = false;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<
      AgentOverlayStore,
      | "setSearchParams"
      | "navigate"
      | "locationState"
      | "currentSearchParams"
      | "shouldCloseUntrackedOverlayRoot"
      | "updateAgentParams"
      | "parseAgentState"
    >(this, {
      agentParam: observable,
      setSearchParams: observable,
      navigate: false,
      locationState: false,
      currentSearchParams: false,
      shouldCloseUntrackedOverlayRoot: false,
      isOpen: computed,
      isEntryPointEnabled: computed,
      isTaskProjectTrackingEnabled: computed,
      isFeatureEnabled: computed,
      isSurfaceEnabled: false,
      isMemoryFeatureEnabled: computed,
      isSkillsFeatureEnabled: computed,
      activeSurface: computed,
      openOverview: action,
      openGoals: action,
      openGoal: action,
      openProjects: action,
      openProject: action,
      openProjectTasks: action,
      openTasks: action,
      openTask: action,
      openInformation: action,
      openInformationItem: action,
      openSourceNote: action,
      openRoutines: action,
      openMemory: action,
      openSkills: action,
      openActivities: action,
      close: action,
      setAgentParam: action,
      updateAgentParams: false,
      parseAgentState: false,
      useSyncAgentSearchParam: action,
    });
  }

  get isOpen(): boolean {
    return this.agentParam !== null && this.isSurfaceEnabled(this.agentParam.surfaceId);
  }

  get isEntryPointEnabled(): boolean {
    return this.store.spaceAccountFeatureFlags.getFlagStatus("agent-overlay") === true;
  }

  /**
   * Legacy accounts require Proactive, while active commercial-catalog plans
   * receive task and project tracking through their metered allowances.
   */
  get isTaskProjectTrackingEnabled(): boolean {
    const commercialRolloutIsActive = isCommercialRolloutActive({
      catalog: this.store.platformInfo.commercialCatalog,
      snapshot: this.store.platformInfo.commercialPlanSnapshot,
    });

    return (
      this.isEntryPointEnabled &&
      (this.store.subscriptions.hasProactiveAccess || commercialRolloutIsActive)
    );
  }

  get isFeatureEnabled(): boolean {
    return this.isEntryPointEnabled && this.store.subscriptions.hasProactiveAccess;
  }

  isSurfaceEnabled(surfaceId: AgentSurfaceId): boolean {
    switch (surfaceId) {
      case AgentSurfaceId.Overview:
      case AgentSurfaceId.Projects:
      case AgentSurfaceId.Project:
      case AgentSurfaceId.ProjectTasks:
      case AgentSurfaceId.Tasks:
      case AgentSurfaceId.Task:
        return this.isTaskProjectTrackingEnabled;
      case AgentSurfaceId.Memory:
        return this.isMemoryFeatureEnabled;
      case AgentSurfaceId.Skills:
        return this.isSkillsFeatureEnabled;
      default:
        return this.isFeatureEnabled;
    }
  }

  get isSkillsFeatureEnabled(): boolean {
    return (
      this.isFeatureEnabled &&
      this.store.spaceAccountFeatureFlags.getFlagStatus("personal-skills") === true
    );
  }

  get isMemoryFeatureEnabled(): boolean {
    return (
      this.isFeatureEnabled &&
      this.store.spaceAccountFeatureFlags.getFlagStatus("memory-lattice") === true
    );
  }

  get activeSurface(): AgentOverlayState {
    return this.agentParam ?? { surfaceId: AgentSurfaceId.Overview };
  }

  openOverview() {
    if (!this.isTaskProjectTrackingEnabled) return;

    this.updateAgentParams({ surfaceId: AgentSurfaceId.Overview });
  }

  openGoals() {
    if (!this.isFeatureEnabled) return;
    this.updateAgentParams({ surfaceId: AgentSurfaceId.Goals });
  }

  openGoal({ goalId }: { goalId: string }) {
    if (!this.isFeatureEnabled || !goalId) return;
    this.updateAgentParams({ surfaceId: AgentSurfaceId.Goal, goalId });
  }

  openProjects() {
    if (!this.isTaskProjectTrackingEnabled) return;

    this.updateAgentParams({ surfaceId: AgentSurfaceId.Projects });
  }

  openProject({ projectId }: { projectId: string }) {
    if (!this.isTaskProjectTrackingEnabled || !projectId) return;

    this.updateAgentParams({
      surfaceId: AgentSurfaceId.Project,
      projectId,
    });
  }

  openProjectTasks({ projectId }: { projectId: string }) {
    if (!this.isTaskProjectTrackingEnabled || !projectId) return;

    this.updateAgentParams({
      surfaceId: AgentSurfaceId.ProjectTasks,
      projectId,
    });
  }

  /**
   * Opens the Tasks surface scoped to `projectId`, or to every project when it
   * is omitted. Either way the project filter is authoritative, so an entry
   * point that means "all tasks" clears a filter left behind by a previous one.
   */
  openTasks({ projectId, view }: { projectId?: string; view?: string } = {}) {
    if (!this.isTaskProjectTrackingEnabled) return;

    this.updateAgentParams(
      { surfaceId: AgentSurfaceId.Tasks },
      { taskProjectId: projectId ?? null, taskView: view }
    );
  }

  openTask({ taskId }: { taskId: string }) {
    if (!this.isTaskProjectTrackingEnabled || !taskId) return;

    this.updateAgentParams({
      surfaceId: AgentSurfaceId.Task,
      taskId,
    });
  }

  openInformation() {
    if (!this.isFeatureEnabled) return;
    this.updateAgentParams({ surfaceId: AgentSurfaceId.Information });
  }

  openInformationItem({ informationItemId }: { informationItemId: string }) {
    if (!this.isFeatureEnabled || !informationItemId) return;
    this.updateAgentParams({
      surfaceId: AgentSurfaceId.InformationItem,
      informationItemId,
    });
  }

  openSourceNote({ noteId }: { noteId: string }) {
    if (!this.isOpen || !this.agentParam || !noteId || !this.currentSearchParams) {
      return;
    }

    const noteSearchParams = withoutAgentSearchParams({
      searchParams: this.currentSearchParams,
    });
    const routeState = { ...(this.locationState ?? {}) };
    delete routeState[AGENT_OVERLAY_HISTORY_STATE_KEY];
    delete routeState[AGENT_OVERLAY_HISTORY_DEPTH_STATE_KEY];
    delete routeState[AGENT_OVERLAY_UNTRACKED_ROOT_STATE_KEY];

    // Push the note without overlay params. This reveals the note immediately,
    // while browser Back restores the exact overlay surface and filters.
    this.store.navigation.goToNote({
      noteId,
      openInSameTab: true,
      replace: false,
      routeSearch: noteSearchParams.toString(),
      routeState: Object.keys(routeState).length > 0 ? routeState : undefined,
    });
  }

  openRoutines() {
    if (!this.isFeatureEnabled) return;

    this.updateAgentParams({ surfaceId: AgentSurfaceId.Routines });
  }

  openMemory() {
    if (!this.isMemoryFeatureEnabled) return;

    this.updateAgentParams({ surfaceId: AgentSurfaceId.Memory });
  }

  openSkills() {
    if (!this.isSkillsFeatureEnabled) return;

    this.updateAgentParams({ surfaceId: AgentSurfaceId.Skills });
  }

  openActivities(context?: AgentActivityContext) {
    if (!this.isFeatureEnabled) return;

    this.updateAgentParams(
      { surfaceId: AgentSurfaceId.Activities },
      {
        activityContext: context ?? null,
      }
    );
  }

  close() {
    if (!this.agentParam) return;

    // Cleared before navigating so a second close — a double Escape, or a
    // double-click on the button — cannot pop a second history entry while the
    // first `popstate` is still in flight. `useSyncAgentSearchParam` re-derives
    // this from the URL, so it self-corrects if the entry still carries params.
    this.setAgentParam(null);

    const overlayHistoryDepth = this.locationState?.[AGENT_OVERLAY_HISTORY_DEPTH_STATE_KEY];
    if (
      this.locationState?.[AGENT_OVERLAY_HISTORY_STATE_KEY] &&
      overlayHistoryDepth &&
      this.navigate
    ) {
      this.shouldCloseUntrackedOverlayRoot =
        this.locationState[AGENT_OVERLAY_UNTRACKED_ROOT_STATE_KEY] === true;
      this.navigate(-overlayHistoryDepth);
      return;
    }

    this.updateAgentParams(null, undefined, true);
  }

  setAgentParam(agentParam: AgentOverlayState | null) {
    this.agentParam = agentParam;
  }

  private updateAgentParams(
    agentState: AgentOverlayState | null,
    options?: {
      // `undefined` leaves the current project filter alone, `null` clears it.
      taskProjectId?: string | null;
      // `undefined` leaves the current task view alone, `null` clears it.
      taskView?: string | null;
      // `undefined` preserves activity context, `null` clears all exact IDs.
      activityContext?: AgentActivityContext | null;
    },
    forceReplace = false
  ) {
    if (!this.setSearchParams) return;
    const setSearchParams = this.setSearchParams;

    const isInitialOpen = this.agentParam === null && agentState !== null;
    const isSameDestination = agentStatesMatch({
      currentState: this.agentParam,
      nextState: agentState,
    });
    const currentOverlayHistoryDepth =
      this.locationState?.[AGENT_OVERLAY_HISTORY_DEPTH_STATE_KEY] ?? 0;
    const shouldAddOverlayHistoryEntry =
      agentState !== null && !isInitialOpen && !isSameDestination && !forceReplace;
    const replace = forceReplace || isSameDestination;
    const nextHistoryState = (() => {
      if (isInitialOpen) {
        return {
          ...(this.locationState ?? {}),
          [AGENT_OVERLAY_HISTORY_STATE_KEY]: true as const,
          [AGENT_OVERLAY_HISTORY_DEPTH_STATE_KEY]: 1,
        };
      }

      if (shouldAddOverlayHistoryEntry) {
        return {
          ...(this.locationState ?? {}),
          [AGENT_OVERLAY_HISTORY_STATE_KEY]: true as const,
          [AGENT_OVERLAY_HISTORY_DEPTH_STATE_KEY]: currentOverlayHistoryDepth + 1,
          ...(currentOverlayHistoryDepth === 0
            ? { [AGENT_OVERLAY_UNTRACKED_ROOT_STATE_KEY]: true as const }
            : {}),
        };
      }

      return this.locationState;
    })();

    // Make the shell respond to the interaction immediately. URL sync remains
    // the durable source of truth and will reconcile this state after history
    // updates, but it should not gate the first visible frame of the overlay.
    this.setAgentParam(agentState);

    startTransition(() => {
      setSearchParams(
        prevParams => {
          const nextParams = withoutAgentIdentitySearchParams({
            searchParams: prevParams,
          });

          if (!agentState) {
            return withoutAgentSearchParams({ searchParams: nextParams });
          }

          const agentParams = withoutSettingsSearchParams({
            searchParams: nextParams,
          });
          agentParams.set(AGENT_SEARCH_PARAM, agentState.surfaceId);

          if (
            agentState.surfaceId === AgentSurfaceId.Project ||
            agentState.surfaceId === AgentSurfaceId.ProjectTasks
          ) {
            agentParams.set(AGENT_PROJECT_ID_SEARCH_PARAM, agentState.projectId);
          }

          if (agentState.surfaceId === AgentSurfaceId.Task) {
            agentParams.set(AGENT_TASK_ID_SEARCH_PARAM, agentState.taskId);
          }

          if (agentState.surfaceId === AgentSurfaceId.Goal) {
            agentParams.set(AGENT_GOAL_ID_SEARCH_PARAM, agentState.goalId);
          }

          if (agentState.surfaceId === AgentSurfaceId.InformationItem) {
            agentParams.set(AGENT_INFORMATION_ITEM_ID_SEARCH_PARAM, agentState.informationItemId);
          }

          if (options?.taskProjectId !== undefined) {
            if (options.taskProjectId) {
              agentParams.set(AGENT_TASK_PROJECT_ID_SEARCH_PARAM, options.taskProjectId);
            } else {
              agentParams.delete(AGENT_TASK_PROJECT_ID_SEARCH_PARAM);
            }
          }

          if (options?.taskView !== undefined) {
            if (options.taskView) {
              agentParams.set(AGENT_TASK_VIEW_SEARCH_PARAM, options.taskView);
            } else {
              agentParams.delete(AGENT_TASK_VIEW_SEARCH_PARAM);
            }
          }

          if (options?.activityContext !== undefined) {
            agentParams.delete(AGENT_ACTIVITY_PROJECT_ID_SEARCH_PARAM);
            agentParams.delete(AGENT_ACTIVITY_TASK_ID_SEARCH_PARAM);
            agentParams.delete(AGENT_ACTIVITY_ROUTINE_ID_SEARCH_PARAM);

            if (options.activityContext?.kind === "project") {
              agentParams.set(
                AGENT_ACTIVITY_PROJECT_ID_SEARCH_PARAM,
                options.activityContext.projectId
              );
            } else if (options.activityContext?.kind === "task") {
              agentParams.set(AGENT_ACTIVITY_TASK_ID_SEARCH_PARAM, options.activityContext.taskId);
            } else if (options.activityContext?.kind === "routine") {
              agentParams.set(
                AGENT_ACTIVITY_ROUTINE_ID_SEARCH_PARAM,
                options.activityContext.routineId
              );
            }
          }

          return agentParams;
        },
        { replace, state: nextHistoryState }
      );
    });
  }

  private parseAgentState({
    rawAgentParam,
    projectId,
    taskId,
    goalId,
    informationItemId,
  }: {
    rawAgentParam: string | null;
    projectId: string | null;
    taskId: string | null;
    goalId: string | null;
    informationItemId: string | null;
  }): AgentOverlayState | null {
    switch (rawAgentParam) {
      case AgentSurfaceId.Overview:
        return { surfaceId: AgentSurfaceId.Overview };
      case AgentSurfaceId.Goals:
        return { surfaceId: AgentSurfaceId.Goals };
      case AgentSurfaceId.Goal:
        return goalId ? { surfaceId: AgentSurfaceId.Goal, goalId } : null;
      case AgentSurfaceId.Projects:
        return { surfaceId: AgentSurfaceId.Projects };
      case AgentSurfaceId.Project:
        return projectId ? { surfaceId: AgentSurfaceId.Project, projectId } : null;
      case AgentSurfaceId.ProjectTasks:
        return projectId ? { surfaceId: AgentSurfaceId.ProjectTasks, projectId } : null;
      case AgentSurfaceId.Tasks:
        return { surfaceId: AgentSurfaceId.Tasks };
      case AgentSurfaceId.Task:
        return taskId ? { surfaceId: AgentSurfaceId.Task, taskId } : null;
      case AgentSurfaceId.Information:
        return { surfaceId: AgentSurfaceId.Information };
      case AgentSurfaceId.InformationItem:
        return informationItemId
          ? { surfaceId: AgentSurfaceId.InformationItem, informationItemId }
          : null;
      case AgentSurfaceId.Routines:
        return { surfaceId: AgentSurfaceId.Routines };
      case AgentSurfaceId.Memory:
        return this.isMemoryFeatureEnabled ? { surfaceId: AgentSurfaceId.Memory } : null;
      case AgentSurfaceId.Skills:
        return this.isSkillsFeatureEnabled ? { surfaceId: AgentSurfaceId.Skills } : null;
      case AgentSurfaceId.Activities:
        return { surfaceId: AgentSurfaceId.Activities };
      default:
        return null;
    }
  }

  /**
   * Syncs the `?agent=<surface-id>` query params into this store.
   *
   * Called from `AppStore.useInitializeAppStoreEffects()`.
   */
  useSyncAgentSearchParam() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    this.setSearchParams = setSearchParams;
    this.navigate = navigate;
    this.currentSearchParams = searchParams;
    this.locationState =
      location.state && typeof location.state === "object"
        ? (location.state as AgentOverlayHistoryState)
        : null;

    const rawAgentParam = searchParams.get(AGENT_SEARCH_PARAM);
    const projectId = searchParams.get(AGENT_PROJECT_ID_SEARCH_PARAM);
    const taskId = searchParams.get(AGENT_TASK_ID_SEARCH_PARAM);
    const goalId = searchParams.get(AGENT_GOAL_ID_SEARCH_PARAM);
    const informationItemId = searchParams.get(AGENT_INFORMATION_ITEM_ID_SEARCH_PARAM);
    const isEntryPointEnabled = this.isEntryPointEnabled;
    const isFeatureEnabled = this.isFeatureEnabled;
    const isMemoryFeatureEnabled = this.isMemoryFeatureEnabled;
    const isSkillsFeatureEnabled = this.isSkillsFeatureEnabled;

    useEffect(() => {
      if (this.shouldCloseUntrackedOverlayRoot) {
        this.shouldCloseUntrackedOverlayRoot = false;
        this.setAgentParam(null);
        this.updateAgentParams(null, undefined, true);
        return;
      }

      if (!isEntryPointEnabled) {
        this.setAgentParam(null);

        if (rawAgentParam) {
          this.updateAgentParams(null, undefined, true);
        }

        return;
      }

      if (!rawAgentParam) {
        this.setAgentParam(null);
        return;
      }

      const agentState = this.parseAgentState({
        rawAgentParam,
        projectId,
        taskId,
        goalId,
        informationItemId,
      });

      if (agentState && this.isSurfaceEnabled(agentState.surfaceId)) {
        this.setAgentParam(agentState);
        return;
      }

      this.setAgentParam(null);
      this.updateAgentParams(null, undefined, true);
    }, [
      rawAgentParam,
      projectId,
      taskId,
      goalId,
      informationItemId,
      isEntryPointEnabled,
      isFeatureEnabled,
      isMemoryFeatureEnabled,
      isSkillsFeatureEnabled,
    ]);
  }
}
